"use client";

import { useMemo } from "react";
import { Heading, Separator, VStack } from "@chakra-ui/react";
import {
  useAssignableSituations,
  useSituationsQuery,
} from "@/features/situations";
import { useInterimStateManager, useUserChoices } from "@/state/globalStore";
import type { ThoughtReassignData } from "@/state/globalStoreSlices/userChoices";
import { mindstringPrototypesSlang } from "@/shared/config/backend";
import { getPrototypeBadgeTokens } from "@/shared/config/prototypeTheme";
import {
  PROFILE_SHAPING_LABELS,
  PROFILE_SHAPING_MESSAGES,
} from "@/features/profile-shaping/config/actions";
import ContentItemDetailModeHeader from "@/shared/ui/display/ContentItemDetailModeHeader";
import GenericDrawerInfo from "@/shared/ui/display/GenericDrawerInfo";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import LoadingState from "@/shared/ui/LoadingState";
import ThoughtAnchorTargetItem from "./ThoughtAnchorTargetItem";
import useThoughtAnchorWorkflow from "./useThoughtAnchorWorkflow";

interface ThoughtAnchorWorkflowProps {
  items: ThoughtReassignData[];
  onCancel?: () => void;
  onSuccess?: () => void;
  clearSelectionOnSuccess?: boolean;
}

export default function ThoughtAnchorWorkflow({
  items,
  onCancel,
  onSuccess,
  clearSelectionOnSuccess = false,
}: ThoughtAnchorWorkflowProps) {
  const situationsQuery = useSituationsQuery();
  const { resetInterimState } = useInterimStateManager();
  const { unselectThoughts } = useUserChoices();
  const allSituations = useMemo(
    () => situationsQuery.data ?? [],
    [situationsQuery.data],
  );
  const validSituationIds = useMemo(
    () => allSituations.map((situation) => situation.id),
    [allSituations],
  );
  const {
    isCurrentDestination,
    isMoving,
    moveThoughtsToSituation,
    movingToSituationId,
    hasNeedsAnchorSource,
    invalidSourceSituationIds,
    sourceSituationIds,
    sourcePrototypes,
    thoughtIds,
  } = useThoughtAnchorWorkflow(items, { validSituationIds });
  const { situations: targetSituations } = useAssignableSituations(
    allSituations,
    sourcePrototypes,
  );

  const selectedThoughtsLabel = useMemo(() => {
    const thoughtLabel = thoughtIds.length === 1 ? "thought" : "thoughts";
    return `${thoughtIds.length} ${thoughtLabel}`;
  }, [thoughtIds.length]);
  const isSingleThought = thoughtIds.length === 1;

  const sourceScopeLabel = useMemo(() => {
    const validSourceSituationCount =
      sourceSituationIds.length - invalidSourceSituationIds.length;

    if (hasNeedsAnchorSource && sourceSituationIds.length === 0) {
      return PROFILE_SHAPING_LABELS.needsAnchor;
    }

    if (
      hasNeedsAnchorSource &&
      invalidSourceSituationIds.length === sourceSituationIds.length
    ) {
      return PROFILE_SHAPING_LABELS.needsAnchor;
    }

    if (sourceSituationIds.length === 0) {
      return PROFILE_SHAPING_LABELS.needsAnchor;
    }

    const situationLabel =
      validSourceSituationCount === 1 ? "situation" : "situations";

    return hasNeedsAnchorSource
      ? `${validSourceSituationCount} ${situationLabel} + needs anchor`
      : `${validSourceSituationCount} ${situationLabel}`;
  }, [
    hasNeedsAnchorSource,
    invalidSourceSituationIds.length,
    sourceSituationIds.length,
  ]);

  const prototypeScopeLabel = useMemo(
    () =>
      sourcePrototypes
        .map((prototype) => mindstringPrototypesSlang[prototype].label)
        .join(", "),
    [sourcePrototypes],
  );
  const prototypeScopeBadge =
    sourcePrototypes.length === 1
      ? getPrototypeBadgeTokens(sourcePrototypes[0])
      : { colorPalette: "gray" as const };
  const returnHeader = onCancel ? (
    <ContentItemDetailModeHeader
      title="Anchor to situation"
      onReturn={onCancel}
      returnAriaLabel="Back to missing anchors"
    />
  ) : null;

  if (situationsQuery.isLoading) {
    return (
      <VStack gap="4" align="stretch">
        {returnHeader}
        <LoadingState message="Loading situations..." />
      </VStack>
    );
  }

  if (situationsQuery.isError) {
    return (
      <VStack gap="4" align="stretch">
        {returnHeader}
        <GenericEmptyComponent
          title="Could not load situations"
          message="Try again in a moment before moving these thoughts."
        />
      </VStack>
    );
  }

  if (allSituations.length === 0) {
    return (
      <VStack gap="4" align="stretch">
        {returnHeader}
        <GenericEmptyComponent
          title="No situations available"
          message="Add a situation before anchoring selected thoughts."
        />
      </VStack>
    );
  }

  const drawerInfo = (
    <GenericDrawerInfo
      description={
        hasNeedsAnchorSource
          ? PROFILE_SHAPING_MESSAGES.anchorNeedsAnchorThoughts
          : `Choose where ${isSingleThought ? "this thought" : "these thoughts"} should be anchored. Only supported situations are shown.`
      }
      items={[
        { label: selectedThoughtsLabel, colorPalette: "blue" },
        { label: `From ${sourceScopeLabel}`, colorPalette: "gray" },
        { label: prototypeScopeLabel, ...prototypeScopeBadge },
      ]}
    />
  );

  if (targetSituations.length === 0) {
    return (
      <VStack gap="4" align="stretch">
        {drawerInfo}
        <Separator />
        {returnHeader}
        <GenericEmptyComponent
          title="No compatible situations"
          message="Add or edit a situation that supports every selected perspective before anchoring."
          icon="situation"
          variant="inline"
        />
      </VStack>
    );
  }

  return (
    <VStack gap="4" align="stretch">
      {drawerInfo}

      <Separator />

      {returnHeader ?? <Heading size="sm">Anchor to situation</Heading>}

      <VStack gap="3" align="stretch">
        {targetSituations.map((situation) => {
          const isPendingTarget = movingToSituationId === situation.id;
          const disabledReason =
            isMoving && !isPendingTarget
              ? "Finish the current anchoring first"
              : undefined;

          return (
            <ThoughtAnchorTargetItem
              key={situation.id}
              situation={situation}
              isCurrent={isCurrentDestination(situation.id)}
              isDisabled={Boolean(disabledReason)}
              isPending={isPendingTarget}
              disabledReason={disabledReason}
              onClick={async () => {
                const didMove = await moveThoughtsToSituation(situation.id);

                if (didMove) {
                  if (clearSelectionOnSuccess) {
                    unselectThoughts(thoughtIds);
                  }
                  resetInterimState("reassign_thought");
                  onSuccess?.();
                }
              }}
            />
          );
        })}
      </VStack>
    </VStack>
  );
}
